import { useState, useEffect, useCallback, useRef } from "react";

const DRAFT_PREFIX = "tabitto-draft-";

interface UseFormDraftOptions<T> {
  /** Unique key for this draft (e.g., "expense-session123") */
  key: string;
  /** Initial form data */
  initialData: T;
  /** Debounce delay in ms (default: 500) */
  debounceMs?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
}

interface UseFormDraftReturn<T> {
  /** Current form data */
  data: T;
  /** Update form data (triggers auto-save) */
  setData: (data: T | ((prev: T) => T)) => void;
  /** Whether there's a saved draft */
  hasDraft: boolean;
  /** Restore draft data */
  restoreDraft: () => void;
  /** Clear the draft */
  clearDraft: () => void;
  /** Save status: "idle" | "saving" | "saved" */
  saveStatus: "idle" | "saving" | "saved";
  /** Reset to initial data and clear draft */
  reset: () => void;
}

/**
 * Hook for auto-saving form data to localStorage with debounce.
 * Helps users recover from interruptions (train exit, meetings, etc.)
 */
export function useFormDraft<T>({
  key,
  initialData,
  debounceMs = 500,
  enabled = true,
}: UseFormDraftOptions<T>): UseFormDraftReturn<T> {
  const storageKey = `${DRAFT_PREFIX}${key}`;
  const [data, setDataInternal] = useState<T>(initialData);
  const [hasDraft, setHasDraft] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);

  const prevEnabledRef = useRef(enabled);

  // Check for existing draft and re-read when enabled transitions to true
  useEffect(() => {
    const wasDisabled = !prevEnabledRef.current;
    prevEnabledRef.current = enabled;

    if (!enabled) return;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if draft is not empty (has meaningful data)
        const hasContent = Object.values(parsed).some((v) => {
          if (typeof v === "string") return v.trim().length > 0;
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === "object" && v !== null) return Object.keys(v).length > 0;
          return v !== null && v !== undefined;
        });
        setHasDraft(hasContent);

        // If transitioning from disabled to enabled, reload data from localStorage
        if (wasDisabled && hasContent) {
          setDataInternal(parsed as T);
          isInitialMount.current = true; // Prevent immediate auto-save
        }
      } else {
        setHasDraft(false);
        // If transitioning from disabled to enabled with no draft, reset to initial
        if (wasDisabled) {
          setDataInternal(initialData);
          isInitialMount.current = true;
        }
      }
    } catch {
      // Invalid data, ignore
      setHasDraft(false);
    }
  }, [storageKey, enabled, initialData]);

  // Auto-save with debounce
  useEffect(() => {
    if (!enabled) return;

    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setSaveStatus("saving");

    debounceRef.current = setTimeout(() => {
      try {
        // Check if data has meaningful content
        const hasContent = Object.values(data as Record<string, unknown>).some((v) => {
          if (typeof v === "string") return v.trim().length > 0;
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === "object" && v !== null) return Object.keys(v).length > 0;
          return v !== null && v !== undefined;
        });

        if (hasContent) {
          localStorage.setItem(storageKey, JSON.stringify(data));
          setHasDraft(true);
        } else {
          // Clear draft if no meaningful content
          localStorage.removeItem(storageKey);
          setHasDraft(false);
        }
        setSaveStatus("saved");

        // Reset to idle after showing "saved"
        setTimeout(() => setSaveStatus("idle"), 1500);
      } catch {
        setSaveStatus("idle");
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [data, storageKey, debounceMs, enabled]);

  const setData = useCallback((newData: T | ((prev: T) => T)) => {
    setDataInternal(newData);
  }, []);

  const restoreDraft = useCallback(() => {
    if (!enabled) return;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as T;
        setDataInternal(parsed);
      }
    } catch {
      // Invalid data, ignore
    }
  }, [storageKey, enabled]);

  const clearDraft = useCallback(() => {
    // Cancel any pending save
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    try {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
      setSaveStatus("idle");
      // Reset data to initial and prevent auto-save from triggering
      setDataInternal(initialData);
      isInitialMount.current = true;
    } catch {
      // Ignore errors
    }
  }, [storageKey, initialData]);

  const reset = useCallback(() => {
    setDataInternal(initialData);
    clearDraft();
    isInitialMount.current = true;
  }, [initialData, clearDraft]);

  return {
    data,
    setData,
    hasDraft,
    restoreDraft,
    clearDraft,
    saveStatus,
    reset,
  };
}

/**
 * Get all draft keys for a given prefix.
 * Useful for showing a list of saved drafts.
 */
export function getDraftKeys(prefix: string): string[] {
  const keys: string[] = [];
  const fullPrefix = `${DRAFT_PREFIX}${prefix}`;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(fullPrefix)) {
        keys.push(key.replace(DRAFT_PREFIX, ""));
      }
    }
  } catch {
    // Ignore errors
  }

  return keys;
}

/**
 * Clear all drafts for a given prefix.
 */
export function clearDrafts(prefix: string): void {
  const keys = getDraftKeys(prefix);
  keys.forEach((key) => {
    try {
      localStorage.removeItem(`${DRAFT_PREFIX}${key}`);
    } catch {
      // Ignore errors
    }
  });
}
